const TILE_SIZES = [8, 10, 12, 16, 20, 24, 32];
const DARK_CHECKER_VALUES = [168, 176, 184, 192, 196, 200, 204, 208, 211, 214, 217, 220, 224, 228];
/** Neutral light pixel that matches common AI “fake transparency” checkerboards. */
export function isFakeTransparencyPixel(r, g, b, x, y) {
    if (Math.abs(r - g) > 18 || Math.abs(g - b) > 18 || Math.abs(r - b) > 18)
        return false;
    const v = (r + g + b) / 3;
    if (v < 155)
        return false;
    for (const tile of TILE_SIZES) {
        const cell = (Math.floor(x / tile) + Math.floor(y / tile)) % 2;
        if (cell === 0) {
            if (v >= 238)
                return true;
        }
        else {
            for (const dark of DARK_CHECKER_VALUES) {
                if (Math.abs(v - dark) <= 22)
                    return true;
            }
        }
    }
    return v >= 248;
}
/**
 * Flood-fill from image edges through fake-transparency pixels and clear their alpha.
 * Returns true when at least one pixel was made transparent.
 */
export function stripFakeTransparencyGrid(data, width, height) {
    if (width <= 0 || height <= 0)
        return false;
    const visited = new Uint8Array(width * height);
    const queue = [];
    const index = (x, y) => y * width + x;
    const trySeed = (x, y) => {
        const i = index(x, y);
        if (visited[i])
            return;
        const pi = i * 4;
        if (data[pi + 3] < 8)
            return;
        const r = data[pi];
        const g = data[pi + 1];
        const b = data[pi + 2];
        if (!isFakeTransparencyPixel(r, g, b, x, y))
            return;
        visited[i] = 1;
        queue.push(x, y);
    };
    for (let x = 0; x < width; x += 1) {
        trySeed(x, 0);
        trySeed(x, height - 1);
    }
    for (let y = 0; y < height; y += 1) {
        trySeed(0, y);
        trySeed(width - 1, y);
    }
    let changed = false;
    let qi = 0;
    while (qi < queue.length) {
        const x = queue[qi++];
        const y = queue[qi++];
        const i = index(x, y);
        const pi = i * 4;
        if (data[pi + 3] > 0) {
            data[pi + 3] = 0;
            changed = true;
        }
        const neighbors = [
            [x - 1, y],
            [x + 1, y],
            [x, y - 1],
            [x, y + 1],
        ];
        for (const [nx, ny] of neighbors) {
            if (nx < 0 || ny < 0 || nx >= width || ny >= height)
                continue;
            const ni = index(nx, ny);
            if (visited[ni])
                continue;
            const npi = ni * 4;
            if (data[npi + 3] < 8)
                continue;
            const r = data[npi];
            const g = data[npi + 1];
            const b = data[npi + 2];
            if (!isFakeTransparencyPixel(r, g, b, nx, ny))
                continue;
            visited[ni] = 1;
            queue.push(nx, ny);
        }
    }
    return changed;
}
function colorNearKey(r, g, b, key, tolerance) {
    return (Math.abs(r - key[0]) <= tolerance &&
        Math.abs(g - key[1]) <= tolerance &&
        Math.abs(b - key[2]) <= tolerance);
}
/**
 * Flood-fill chroma-key pixels from image edges and clear alpha (U12).
 * Edge-only flood-fill avoids punching interior regions of the same color.
 */
export function chromaKeyColor(data, width, height, keyRgb, tolerance = 28) {
    if (width <= 0 || height <= 0)
        return false;
    const visited = new Uint8Array(width * height);
    const queue = [];
    const index = (x, y) => y * width + x;
    const trySeed = (x, y) => {
        const i = index(x, y);
        if (visited[i])
            return;
        const pi = i * 4;
        if (data[pi + 3] < 8)
            return;
        const r = data[pi];
        const g = data[pi + 1];
        const b = data[pi + 2];
        if (!colorNearKey(r, g, b, keyRgb, tolerance))
            return;
        visited[i] = 1;
        queue.push(x, y);
    };
    for (let x = 0; x < width; x += 1) {
        trySeed(x, 0);
        trySeed(x, height - 1);
    }
    for (let y = 0; y < height; y += 1) {
        trySeed(0, y);
        trySeed(width - 1, y);
    }
    let changed = false;
    let qi = 0;
    while (qi < queue.length) {
        const x = queue[qi++];
        const y = queue[qi++];
        const i = index(x, y);
        const pi = i * 4;
        if (data[pi + 3] > 0) {
            data[pi + 3] = 0;
            changed = true;
        }
        const neighbors = [
            [x - 1, y],
            [x + 1, y],
            [x, y - 1],
            [x, y + 1],
        ];
        for (const [nx, ny] of neighbors) {
            if (nx < 0 || ny < 0 || nx >= width || ny >= height)
                continue;
            const ni = index(nx, ny);
            if (visited[ni])
                continue;
            const npi = ni * 4;
            if (data[npi + 3] < 8)
                continue;
            const r = data[npi];
            const g = data[npi + 1];
            const b = data[npi + 2];
            if (!colorNearKey(r, g, b, keyRgb, tolerance))
                continue;
            visited[ni] = 1;
            queue.push(nx, ny);
        }
    }
    return changed;
}
/** @deprecated prefer chromaKeyColor */
export function stripChromaKeyBackground(data, width, height, keyRgb, tolerance = 28) {
    return chromaKeyColor(data, width, height, keyRgb, tolerance);
}
